/**
 * QueryProvider - Provider do TanStack Query (React Query) com persistência.
 *
 * Fonte única de cache de "server state". No app nativo o cache é persistido
 * em AsyncStorage (offline-first). Na web ele é persistido em IndexedDB
 * (assíncrono) e restrito a uma allowlist — nunca em localStorage, cuja leitura
 * e serialização SÍNCRONAS na main thread travavam celulares modestos.
 *
 * Requisito de persistência: gcTime >= maxAge, senão as queries são coletadas
 * antes de poderem ser reidratadas.
 */

import React from 'react';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';

import { webIdbStorage } from '../lib/webQueryPersister';

// Tempo máximo que um cache persistido é considerado válido ao reidratar.
//
// FILOSOFIA (local-first / stale-while-revalidate): o cache local é a fonte do
// que aparece na tela; a rede é só um upgrade em segundo plano. Por isso o cache
// NÃO deve expirar por tempo para o vazio — "dado de ontem mostrado na hora" é
// melhor que "skeleton frio esperando a rede". A frescura é garantida pelo
// refetch-ao-focar/voltar-do-background que as telas já fazem (ProfileScreen,
// CommunityScreen), não por jogar o cache fora.
//
// HISTÓRICO: era 24h. Isso causava o bug "passei >1 dia sem abrir → Perfil e
// Comunidade ficam carregando": ao reidratar, o React Query descartava qualquer
// cache mais velho que maxAge → tela fria. 30 dias remove esse penhasco na
// prática (quem some por 30 dias pega 1 carga fria — aceitável); o tamanho em
// AsyncStorage segue limitado pelo gcTime/uso real.
const PERSIST_MAX_AGE = 1000 * 60 * 60 * 24 * 30; // 30 dias
const WEB_GC_TIME = 30 * 60 * 1000; // sessão web: 30 min após a última observação
const QUERY_CACHE_STORAGE_KEY = 'RQ_CACHE';

// ── Persistência WEB (mínima, em IndexedDB) ──────────────────────────────────
// Sem ela, TODA recarga de aba é partida fria: o aluno recarrega e o Perfil
// volta a carregar do zero. Não é o mesmo que o offline-first do nativo — é uma
// ponte para a recarga, e por isso é deliberadamente pequena:
//   • IndexedDB (assíncrono) em vez de localStorage. O `AsyncStorage` do RN Web
//     É o localStorage, SÍNCRONO na main thread — foi por isso que a
//     persistência web foi removida antes, e não queremos aquilo de volta.
//   • ALLOWLIST: só a query agregada do Perfil. Persistir o feed traria todas as
//     páginas já carregadas do infinite query, que é justamente o volume que
//     tornava o blob antigo caro. Ampliar só com medição.
//   • 24h de validade: cobre "recarreguei a aba", não "sumi por um mês".
const WEB_PERSIST_MAX_AGE = 1000 * 60 * 60 * 24; // 24h
const WEB_QUERY_CACHE_STORAGE_KEY = 'RQ_CACHE_WEB_V1';

/** Só estas queries são gravadas na web. Manter curta e justificada. */
const isWebPersistableQuery = (query) => {
    const key = query.queryKey;
    return Array.isArray(key) && key[0] === 'profile' && key[1] === 'screen';
};

// Versão do cache persistido. Incrementar para invalidar caches antigos
// quando o formato dos dados mudar (ex.: novas colunas, novo shape).
// rq-v3 (2026-06-07): mudança na camada de dados do Perfil (nova query agregada
// `['profile','screen']`) e do feed (RPC get_feed). Bump precaucional → cada device
// descarta o cache persistido antigo no update (1 carga fria única; depois volta ao
// instantâneo). Evita qualquer estado persistido incompatível em produção.
const PERSIST_BUSTER = 'rq-v3';

// Não transforme um request que já atingiu o teto global de 10s em 3 requests
// consecutivos (10s + backoff + 10s + backoff + 10s). Timeout/abort e erros 4xx
// precisam de ação do usuário ou correção de contrato, não de retry automático.
// Para falhas realmente transitórias (5xx/fetch), uma única nova tentativa basta.
const shouldRetryQuery = (failureCount, error) => {
    const name = error?.name || '';
    const status = Number(error?.status || error?.statusCode || 0);

    if (name === 'TimeoutError' || name === 'AbortError') return false;
    if (status >= 400 && status < 500) return false;

    return failureCount < 1;
};

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            // Tempo que dados são considerados "frescos" (não refetch)
            staleTime: 5 * 60 * 1000, // 5 minutos

            // Nativo mantém cache offline persistido. Na web, um gcTime curto
            // impede a SPA de acumular feeds/vídeos durante toda a sessão.
            gcTime: Platform.OS === 'web' ? WEB_GC_TIME : PERSIST_MAX_AGE,

            // Offline-first: a queryFn EXECUTA uma vez mesmo sem rede, em vez de
            // ficar 'paused' (comportamento do default 'online'). Isso é essencial
            // aqui porque (a) no nativo o cache é persistido em AsyncStorage;
            // e (b) várias queryFns, inclusive na web, caem para dados LOCAIS
            // (storageService) quando o servidor falha — no modo 'online' elas
            // seriam pausadas e o fallback local nunca
            // rodaria. Só os RETRIES pausam offline (evita martelar a rede morta).
            networkMode: 'offlineFirst',

            // O gargalo confirmado é do navegador móvel. Nativo mantém sua
            // política já estabilizada; web evita a cascata de até 30s.
            retry: Platform.OS === 'web' ? shouldRetryQuery : 2,
            retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

            // Performance mobile: não refetch em foco/reconexão automaticamente
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,

            // Mantém dados anteriores enquanto refetch (UX suave)
            placeholderData: (previousData) => previousData,
        },
        mutations: {
            // Mesmo racional: tenta a mutation uma vez mesmo offline (cai em erro
            // tratável) em vez de pausar silenciosamente num estado ambíguo.
            networkMode: 'offlineFirst',
            retry: 1,
        },
    },
});

const asyncStoragePersister = createAsyncStoragePersister({
    storage: AsyncStorage,
    key: QUERY_CACHE_STORAGE_KEY,
    throttleTime: 1000,
});

// `throttleTime` maior que o do nativo: na web cada escrita é uma transação de
// IndexedDB, e o ganho está na recarga — não em gravar a cada tecla.
const webIdbPersister = createAsyncStoragePersister({
    storage: webIdbStorage,
    key: WEB_QUERY_CACHE_STORAGE_KEY,
    throttleTime: 2000,
});

/**
 * QueryProvider Component
 * Web: reidrata da allowlist em IndexedDB. Nativo: reidrata e persiste via AsyncStorage.
 */
export const QueryProvider = ({ children }) => {
    React.useEffect(() => {
        if (Platform.OS !== 'web') return;

        // AsyncStorage usa window.localStorage no React Native Web. O cache
        // persistido antigo reunia até 30 dias de queries num único JSON
        // síncrono: restore/JSON.parse bloqueava o início das queries e cada
        // update reserializava o blob completo em celulares modestos. Na web,
        // rede + cache HTTP já cobrem a sessão; remova só o server-state antigo,
        // sem tocar na sessão Supabase nem no cache pequeno do AuthContext.
        AsyncStorage.removeItem(QUERY_CACHE_STORAGE_KEY).catch(() => {});
    }, []);

    if (Platform.OS === 'web') {
        return (
            <PersistQueryClientProvider
                client={queryClient}
                persistOptions={{
                    persister: webIdbPersister,
                    maxAge: WEB_PERSIST_MAX_AGE,
                    buster: PERSIST_BUSTER,
                    dehydrateOptions: {
                        shouldDehydrateQuery: (query) =>
                            query.state.status === 'success' && isWebPersistableQuery(query),
                    },
                }}
            >
                {children}
            </PersistQueryClientProvider>
        );
    }

    return (
        <PersistQueryClientProvider
            client={queryClient}
            persistOptions={{
                persister: asyncStoragePersister,
                maxAge: PERSIST_MAX_AGE,
                buster: PERSIST_BUSTER,
                dehydrateOptions: {
                    // Só persiste queries que terminaram com sucesso
                    shouldDehydrateQuery: (query) => query.state.status === 'success',
                },
            }}
        >
            {children}
        </PersistQueryClientProvider>
    );
};

// Exporta o queryClient para uso em prefetch e invalidação manual
export { queryClient };

export default QueryProvider;
